import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  isOnline, makeClientId, stashPendingOrder, syncPending, pendingCount, fetchAndCacheBootstrap,
} from "@/lib/offline";
import { money, asset } from "@/lib/format";
import { printOrderJobs } from "@/lib/print";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Minus, Trash2, Search, Leaf, Beef, ShoppingCart, Utensils, Bike, PackageOpen,
  X, Loader2, Lock, Wifi, WifiOff,
} from "lucide-react";

const ORDER_TYPES = [
  { key: "dine-in", label: "Dine In", icon: Utensils },
  { key: "takeaway", label: "Takeaway", icon: PackageOpen },
  { key: "delivery", label: "Delivery", icon: Bike },
];

export default function POS() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [orderType, setOrderType] = useState("dine-in");
  const [tableId, setTableId] = useState("");
  const [terminalId, setTerminalId] = useState("");
  const [customer, setCustomer] = useState({ name: "", phone: "" });
  const [discount, setDiscount] = useState(0);
  const [redeem, setRedeem] = useState(0);
  const [loyaltyPts, setLoyaltyPts] = useState(0);
  const [payOpen, setPayOpen] = useState(false);
  const [splits, setSplits] = useState([{ method: "cash", amount: 0 }]);
  const [edcMeta, setEdcMeta] = useState({ rrn: "", auth_code: "" });
  const [tip, setTip] = useState(0);
  const [addonPick, setAddonPick] = useState(null); // { item, selected: [] }
  const [processing, setProcessing] = useState(false);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [offlinePending, setOfflinePending] = useState(0);

  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data: menu = [] } = useQuery({ queryKey: ["menu"], queryFn: async () => (await api.get("/menu")).data });
  const { data: tables = [] } = useQuery({ queryKey: ["tables"], queryFn: async () => (await api.get("/tables")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: dayStatus } = useQuery({
    queryKey: ["day-end-status"],
    queryFn: async () => (await api.get("/day-end/status")).data,
    refetchInterval: 20000,
  });

  useEffect(() => {
    const refreshPending = () => pendingCount().then(setOfflinePending).catch(() => {});
    refreshPending();
    if (isOnline()) fetchAndCacheBootstrap().catch(() => {});
    const onOn = () => {
      setOnline(true);
      syncPending().then((r) => {
        if (r.synced) toast.success(`Synced ${r.synced} offline order(s)`);
        refreshPending();
      }).catch(() => {});
      fetchAndCacheBootstrap().catch(() => {});
    };
    const onOff = () => setOnline(false);
    window.addEventListener("online", onOn);
    window.addEventListener("offline", onOff);
    return () => {
      window.removeEventListener("online", onOn);
      window.removeEventListener("offline", onOff);
    };
  }, []);

  useEffect(() => {
    if (!terminalId || !online) return;
    const beat = () => { api.post(`/terminals/${terminalId}/heartbeat`).catch(() => {}); };
    beat();
    const t = setInterval(beat, 60000);
    return () => clearInterval(t);
  }, [terminalId, online]);

  const { data: terminals = [] } = useQuery({
    queryKey: ["terminals"],
    queryFn: async () => (await api.get("/terminals")).data,
  });

  const sym = settings?.currency_symbol || "₹";
  const dayLocked = Boolean(dayStatus?.locked);
  const selectedTable = tables.find((t) => t.id === tableId);
  const areaTax = selectedTable?.area_tax_rate != null ? Number(selectedTable.area_tax_rate) : null;

  useEffect(() => {
    const t = searchParams.get("table");
    const ot = searchParams.get("type");
    if (t) {
      setOrderType("dine-in");
      setTableId(t);
    } else if (ot === "delivery" || ot === "takeaway" || ot === "dine-in") {
      setOrderType(ot);
    }
  }, [searchParams]);

  const filtered = useMemo(() => menu.filter((m) => m.available
    && (activeCat === "all" || m.category_id === activeCat)
    && (!search || m.name.toLowerCase().includes(search.toLowerCase()))), [menu, activeCat, search]);

  const addToCart = (item) => {
    const addons = Array.isArray(item.addons) ? item.addons.filter((a) => a && a.name) : [];
    if (addons.length) {
      setAddonPick({ item, selected: [] });
      return;
    }
    pushCartLine(item, []);
  };

  const pushCartLine = (item, selectedAddons) => {
    const tax_rate = areaTax != null ? areaTax : (item.tax_rate ?? 5);
    const addonTotal = selectedAddons.reduce((s, a) => s + Number(a.price || 0), 0);
    const price = Number(item.price) + addonTotal;
    const name = selectedAddons.length
      ? `${item.name} (${selectedAddons.map((a) => a.name).join(", ")})`
      : item.name;
    const key = `${item.id}::${selectedAddons.map((a) => a.name).join("|")}`;
    setCart((c) => {
      const ex = c.find((x) => x.line_key === key);
      if (ex) return c.map((x) => x.line_key === key ? { ...x, qty: x.qty + 1 } : x);
      return [...c, {
        menu_item_id: item.id, line_key: key, name, price, qty: 1, tax_rate,
        addons: selectedAddons,
      }];
    });
  };

  const confirmAddons = () => {
    if (!addonPick) return;
    pushCartLine(addonPick.item, addonPick.selected);
    setAddonPick(null);
  };
  const changeQty = (lineKey, d) => setCart((c) => c.map((x) => (x.line_key || x.menu_item_id) === lineKey ? { ...x, qty: Math.max(1, x.qty + d) } : x));
  const removeItem = (lineKey) => setCart((c) => c.filter((x) => (x.line_key || x.menu_item_id) !== lineKey));

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const tax = cart.reduce((s, i) => s + i.price * i.qty * ((i.tax_rate || 5) / 100), 0);
  const tipAmt = Number(tip || 0);
  const total = Math.max(subtotal + tax - Number(discount || 0) + tipAmt, 0);
  const splitSum = splits.reduce((s, x) => s + Number(x.amount || 0), 0);

  const openPay = () => {
    if (dayLocked) return toast.error("Day is locked — unlock Day End before taking payments");
    if (!cart.length) return toast.error("Cart is empty");
    setTip(0);
    const base = Math.max(subtotal + tax - Number(discount || 0), 0);
    setSplits([{ method: "cash", amount: Number(base.toFixed(2)) }]);
    setPayOpen(true);
  };

  const finalizeOrder = async () => {
    if (dayLocked) return toast.error("Day is locked — unlock Day End before taking payments");
    if (Math.abs(splitSum - total) > 0.5) return toast.error("Split amounts must equal the total");
    setProcessing(true);
    let customer_id = null;
    try {
      if (customer.phone) {
        const { data: cust } = await api.post("/customers", { name: customer.name || "Guest", phone: customer.phone });
        customer_id = cust.id;
      }
      const { data: order } = await api.post("/orders", {
        order_type: orderType,
        table_id: tableId || null,
        customer_id,
        customer_name: customer.name,
        customer_phone: customer.phone,
        items: cart,
        discount: Number(discount || 0),
        terminal_id: terminalId || undefined,
        channel: "pos",
      });
      const edcSplits = splits.filter((s) => s.method === "edc");
      const soleEdc = edcSplits.length === 1 && splits.length === 1;
      for (const s of edcSplits) {
        await api.post("/payments/edc", {
          order_id: order.id,
          amount: Number(s.amount),
          tip: soleEdc ? Number(tip || 0) : 0,
          settle: soleEdc,
          terminal_id: terminalId || undefined,
          rrn: edcMeta.rrn || undefined,
          auth_code: edcMeta.auth_code || undefined,
        });
      }
      if (!soleEdc) {
        await api.post(`/orders/${order.id}/pay`, {
          splits: splits.map((s) => ({
            method: s.method,
            amount: Number(s.amount),
          })),
          redeem_points: Number(redeem || 0),
          tip: Number(tip || 0),
        });
      }
      try { await printOrderJobs(order.id, "kot", { settings }); } catch { /* ignore */ }
      try { await printOrderJobs(order.id, "invoice", { settings }); } catch { /* ignore */ }
      toast.success(`Order ${order.order_number} paid · ${money(total, sym)}`);
      setCart([]); setDiscount(0); setTableId(""); setCustomer({ name: "", phone: "" });
      setRedeem(0); setTip(0); setEdcMeta({ rrn: "", auth_code: "" }); setPayOpen(false);
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["day-end-status"] });
    } catch (e) {
      const offlineish = !isOnline() || !e.response || e.code === "ERR_NETWORK";
      if (offlineish) {
        try {
          await stashPendingOrder({
            client_id: makeClientId(),
            order_type: orderType,
            table_id: tableId || null,
            customer_id: customer_id || undefined,
            customer_name: customer.name,
            customer_phone: customer.phone,
            items: cart,
            discount: Number(discount || 0),
            tip: Number(tip || 0),
            redeem_points: Number(redeem || 0),
            terminal_id: terminalId || undefined,
            channel: "pos",
            payment_status: "paid",
            payments: splits.map((s) => ({
              method: s.method,
              amount: Number(s.amount),
            })),
            paid_at: new Date().toISOString(),
          });
          setOfflinePending(await pendingCount());
          toast.success("Offline — order queued; will sync when back online");
          setCart([]); setDiscount(0); setTableId(""); setCustomer({ name: "", phone: "" });
          setRedeem(0); setTip(0); setEdcMeta({ rrn: "", auth_code: "" }); setPayOpen(false);
        } catch {
          toast.error(formatApiError(e.response?.data?.detail) || "Failed to process order");
        }
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to process order");
      }
    } finally {
      setProcessing(false);
    }
  };

  const sendToKitchen = async () => {
    if (!cart.length) return toast.error("Cart is empty");
    try {
      let customer_id = null;
      if (customer.phone) {
        const { data: cust } = await api.post("/customers", { name: customer.name || "Guest", phone: customer.phone });
        customer_id = cust.id;
      }
      if (orderType === "dine-in" && tableId) {
        const { data: running } = await api.get(`/orders?table_id=${tableId}&unpaid=true`);
        if (running.length) {
          await api.post(`/orders/${running[0].id}/add-items`, { items: cart });
          toast.success(`Added to Table bill ${running[0].order_number}`);
          setCart([]); setDiscount(0);
          qc.invalidateQueries({ queryKey: ["tables"] });
          return;
        }
      }
      const { data: order } = await api.post("/orders", {
        order_type: orderType,
        table_id: tableId || null,
        customer_id,
        customer_name: customer.name,
        customer_phone: customer.phone,
        items: cart,
        discount: Number(discount || 0),
        terminal_id: terminalId || undefined,
        channel: "pos",
      });
      await api.put(`/orders/${order.id}`, { status: "preparing" });
      try { await printOrderJobs(order.id, "kot", { settings }); } catch { /* ignore */ }
      toast.success(`Order ${order.order_number} sent to kitchen (KOT)`);
      setCart([]); setDiscount(0); setTableId(""); setCustomer({ name: "", phone: "" });
      qc.invalidateQueries({ queryKey: ["tables"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send order");
    }
  };

  return (
    <div className="flex h-full">
      <div className="flex-1 flex flex-col overflow-hidden border-r border-[var(--kul-border)]">
        
        {(!online || offlinePending > 0) && (
          <div className={`px-4 py-2 border-b text-sm flex items-center gap-2 ${online ? "bg-sky-50 border-sky-200 text-sky-900" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="offline-badge">
            {online ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            {online ? `Online · ${offlinePending} queued` : "Offline — sales will sync when connected"}
            {online && offlinePending > 0 && (
              <button type="button" className="ml-auto underline text-xs" onClick={() => syncPending().then((r) => { toast.success(`Synced ${r.synced}`); pendingCount().then(setOfflinePending); })}>Sync now</button>
            )}
          </div>
        )}
{dayLocked && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-900 text-sm flex items-center gap-2">
            <Lock className="w-4 h-4" /> Day is locked — payments are paused until Day End unlock.
          </div>
        )}
        <div className="p-4 bg-white border-b border-[var(--kul-border)]">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search menu…"
              className="pl-9 h-11" data-testid="pos-search" />
          </div>
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
            <CatChip active={activeCat === "all"} onClick={() => setActiveCat("all")} label="All" />
            {cats.map((c) => (
              <CatChip key={c.id} active={activeCat === c.id} onClick={() => setActiveCat(c.id)} label={c.name} />
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-[#FAFAFA]">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map((item) => (
              <button key={item.id} type="button" onClick={() => addToCart(item)}
                className="kul-card overflow-hidden text-left flex flex-col"
                data-testid={`menu-item-${item.id}`}>
                <div className="h-24 bg-neutral-100 relative">
                  {item.image && <img src={asset(item.image)} alt={item.name} className="w-full h-full object-cover" />}
                  <span className="absolute top-1.5 left-1.5">
                    {item.is_veg
                      ? <span className="w-4 h-4 rounded border border-green-600 bg-white flex items-center justify-center"><Leaf className="w-2.5 h-2.5 text-green-600" /></span>
                      : <span className="w-4 h-4 rounded border border-red-600 bg-white flex items-center justify-center"><Beef className="w-2.5 h-2.5 text-red-600" /></span>}
                  </span>
                </div>
                <div className="p-2.5 flex-1 flex flex-col">
                  <div className="text-sm font-medium leading-tight line-clamp-2">{item.name}</div>
                  <div className="mt-auto pt-2 font-semibold text-[var(--kul-primary)]">{money(item.price, sym)}</div>
                </div>
              </button>
            ))}
          </div>
          {!filtered.length && <div className="text-center text-neutral-400 py-20">No items found.</div>}
        </div>
      </div>

      <div className="w-[380px] shrink-0 bg-white flex flex-col" data-testid="pos-cart">
        <div className="p-4 border-b border-[var(--kul-border)] space-y-2">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingCart className="w-5 h-5 text-[var(--kul-primary)]" />
            <h2 className="font-display font-800 text-lg">Active Ticket</h2>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {ORDER_TYPES.map((t) => (
              <button key={t.key} type="button" onClick={() => setOrderType(t.key)} data-testid={`order-type-${t.key}`}
                className={`flex flex-col items-center gap-1 py-2 rounded-lg border text-xs font-medium transition-colors ${
                  orderType === t.key ? "border-[var(--kul-primary)] bg-[var(--kul-primary)]/10 text-[var(--kul-primary)]" : "border-neutral-200 text-neutral-500"}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
          {orderType === "dine-in" && (
            <select value={tableId} onChange={(e) => setTableId(e.target.value)} data-testid="pos-table-select"
              className="w-full h-9 rounded-lg border border-neutral-200 px-2 text-sm">
              <option value="">Select table…</option>
              {tables.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.area_name ? ` · ${t.area_name}` : ""}{t.area_tax_rate != null ? ` (${t.area_tax_rate}% tax)` : ""}
                </option>
              ))}
            </select>
          )}
          {!!terminals.length && (
            <select value={terminalId} onChange={(e) => setTerminalId(e.target.value)}
              className="w-full h-9 rounded-lg border border-neutral-200 px-2 text-sm" data-testid="pos-terminal">
              <option value="">Billing terminal…</option>
              {terminals.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.station || t.code}</option>)}
            </select>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {!cart.length && (
            <div className="text-center text-neutral-400 py-16 text-sm">
              <ShoppingCart className="w-10 h-10 mx-auto mb-2 opacity-30" />
              Tap menu items to build an order
            </div>
          )}
          {cart.map((it) => {
            const lk = it.line_key || it.menu_item_id;
            return (
            <div key={lk} className="flex items-center gap-2 p-2 rounded-lg bg-neutral-50">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{it.name}</div>
                <div className="text-xs text-neutral-400">{money(it.price, sym)}</div>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => changeQty(lk, -1)} className="w-6 h-6 rounded bg-white border flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                <span className="w-6 text-center text-sm font-semibold">{it.qty}</span>
                <button type="button" onClick={() => changeQty(lk, 1)} className="w-6 h-6 rounded bg-white border flex items-center justify-center"><Plus className="w-3 h-3" /></button>
              </div>
              <span className="w-16 text-right text-sm font-semibold">{money(it.price * it.qty, sym)}</span>
              <button type="button" onClick={() => removeItem(lk)} className="text-neutral-300 hover:text-[var(--kul-danger)]"><Trash2 className="w-4 h-4" /></button>
            </div>
            );
          })}
        </div>

        <div className="border-t border-[var(--kul-border)] p-4 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Customer name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="h-9 text-sm" />
            <Input placeholder="Phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
              onBlur={async () => {
                if (!customer.phone) { setLoyaltyPts(0); return; }
                try {
                  const { data } = await api.get(`/loyalty/${encodeURIComponent(customer.phone)}`);
                  setLoyaltyPts(data.found ? data.loyalty_points : 0);
                } catch { setLoyaltyPts(0); }
              }}
              className="h-9 text-sm" />
          </div>
          <div className="text-sm space-y-1 pt-1">
            <Row label="Subtotal" value={money(subtotal, sym)} />
            <Row label="GST / Tax" value={money(tax, sym)} />
            <div className="flex items-center justify-between text-neutral-500">
              <span>Discount</span>
              <div className="flex items-center gap-1">
                <span>{sym}</span>
                <Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="h-7 w-20 text-right text-sm" />
              </div>
            </div>
            {loyaltyPts > 0 && (
              <div className="flex items-center justify-between text-neutral-500">
                <span>Redeem pts ({loyaltyPts} avail)</span>
                <Input type="number" value={redeem} onChange={(e) => setRedeem(Math.min(Number(e.target.value || 0), loyaltyPts))} className="h-7 w-20 text-right text-sm" />
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t mt-1">
              <span className="font-display font-800 text-lg">Total</span>
              <span className="font-display font-800 text-xl text-[var(--kul-primary)]" data-testid="pos-total">{money(total, sym)}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button variant="outline" onClick={sendToKitchen} data-testid="pos-kot" className="h-11">Send KOT</Button>
            <Button onClick={openPay} disabled={dayLocked} data-testid="pos-pay-btn"
              className="h-11 bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white font-bold">
              Pay {money(total, sym)}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md" data-testid="pay-dialog">
          <DialogHeader><DialogTitle className="font-display">Split & Pay · {money(total, sym)}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {splits.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <select value={s.method} onChange={(e) => setSplits(splits.map((x, xi) => xi === i ? { ...x, method: e.target.value } : x))}
                  className="h-10 rounded-lg border px-2 text-sm flex-1" data-testid={`split-method-${i}`}>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="upi">UPI</option>
                  <option value="edc">EDC / Card machine</option>
                </select>
                <Input type="number" value={s.amount} onChange={(e) => setSplits(splits.map((x, xi) => xi === i ? { ...x, amount: e.target.value } : x))}
                  className="h-10 w-32 text-right" />
                {splits.length > 1 && <button type="button" onClick={() => setSplits(splits.filter((_, xi) => xi !== i))}><X className="w-4 h-4 text-neutral-400" /></button>}
              </div>
            ))}
            {splits.some((s) => s.method === "edc") && (
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="RRN / Ref" value={edcMeta.rrn} onChange={(e) => setEdcMeta({ ...edcMeta, rrn: e.target.value })} />
                <Input placeholder="Auth code" value={edcMeta.auth_code} onChange={(e) => setEdcMeta({ ...edcMeta, auth_code: e.target.value })} />
              </div>
            )}
            <button type="button" onClick={() => setSplits([...splits, { method: "card", amount: Number((total - splitSum).toFixed(2)) }])}
              className="text-sm text-[var(--kul-primary)] font-medium flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add split
            </button>
            <div>
              <label className="text-xs text-neutral-500">Tip (optional)</label>
              <Input type="number" value={tip} onChange={(e) => {
                const v = e.target.value;
                setTip(v);
                const nextTotal = Math.max(subtotal + tax - Number(discount || 0) + Number(v || 0), 0);
                setSplits((s) => (s.length === 1 ? [{ ...s[0], amount: Number(nextTotal.toFixed(2)) }] : s));
              }} className="h-9 mt-1" data-testid="pos-tip" />
            </div>
            <div className="flex items-center justify-between text-sm p-2 rounded-lg bg-neutral-50">
              <span>Entered</span>
              <span className={`font-semibold ${Math.abs(splitSum - total) < 0.5 ? "text-green-600" : "text-[var(--kul-danger)]"}`}>{money(splitSum, sym)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={finalizeOrder} disabled={processing} data-testid="confirm-pay"
              className="w-full h-11 bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white font-bold">
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : `Confirm Payment · ${money(total, sym)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!addonPick} onOpenChange={(o) => { if (!o) setAddonPick(null); }}>
        <DialogContent className="max-w-sm" data-testid="addon-dialog">
          <DialogHeader><DialogTitle className="font-display">Add-ons · {addonPick?.item?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(addonPick?.item?.addons || []).filter((a) => a?.name).map((a) => {
              const on = addonPick.selected.some((s) => s.name === a.name);
              return (
                <label key={a.name} className="flex items-center justify-between gap-2 p-2 rounded-lg border cursor-pointer hover:bg-neutral-50">
                  <span className="text-sm flex items-center gap-2">
                    <input type="checkbox" checked={on} onChange={() => {
                      setAddonPick((p) => {
                        const selected = on
                          ? p.selected.filter((s) => s.name !== a.name)
                          : [...p.selected, { name: a.name, price: Number(a.price || 0) }];
                        return { ...p, selected };
                      });
                    }} />
                    {a.name}
                  </span>
                  <span className="text-sm font-mono-k">+{money(Number(a.price || 0), sym)}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddonPick(null)}>Skip</Button>
            <Button onClick={confirmAddons} className="bg-[var(--kul-primary)] text-white">Add to cart</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CatChip({ active, onClick, label }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors border ${
        active ? "bg-[var(--kul-charcoal)] text-white border-[var(--kul-charcoal)]" : "bg-white text-neutral-600 border-neutral-200"}`}>
      {label}
    </button>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between text-neutral-500"><span>{label}</span><span>{value}</span></div>;
}
